/**
 * LeadFilterChips — click-to-filter chip cluster shared by /admin/leads
 * and /sales/leads. Renders top categories + tags as toggle chips with
 * counts. Selection is mirrored to URL params so refresh / share works:
 *   ?categories=金融,电子&tags=USDT,求购
 *
 * Filter semantics (matches backend):
 *   - categories: OR  — a lead matches if its category is any of these
 *   - tags:       AND — a lead matches only if it has ALL of these
 *   - the two dimensions are AND-combined with each other
 *
 * Parent owns the canonical selection (from useSearchParams) so it can
 * pass the values down to the list query. This component just renders +
 * emits change events.
 */

import { useQuery } from '@tanstack/react-query';
import { type LeadTagStatsResponse } from '@tg-leads/contracts';

import { api } from '../lib/api';

export interface LeadFilterSelection {
  categories: string[];
  tags: string[];
}

export function LeadFilterChips({
  statsUrl,
  selection,
  onChange,
}: {
  /** Either Routes.admin.leadTagStats or Routes.sales.leadTagStats. */
  statsUrl: string;
  selection: LeadFilterSelection;
  onChange: (next: LeadFilterSelection) => void;
}) {
  const stats = useQuery({
    queryKey: ['leadTagStats', statsUrl],
    queryFn: () => api.get<LeadTagStatsResponse>(statsUrl),
    // Counts shift as new leads land; 60s is a good compromise between
    // freshness and badge churn.
    refetchInterval: 60_000,
  });

  const categories = stats.data?.categories ?? [];
  const tags = stats.data?.tags ?? [];

  const toggleIn = (list: string[], v: string): string[] =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const onToggleCategory = (name: string) => {
    onChange({ ...selection, categories: toggleIn(selection.categories, name) });
  };
  const onToggleTag = (name: string) => {
    onChange({ ...selection, tags: toggleIn(selection.tags, name) });
  };
  const onClearAll = () => onChange({ categories: [], tags: [] });

  const totalSelected = selection.categories.length + selection.tags.length;

  if (stats.isLoading) {
    return (
      <div className="card text-xs text-slate-500">加载筛选项…</div>
    );
  }
  if (categories.length === 0 && tags.length === 0) {
    return (
      <div className="card text-xs text-slate-500">
        还没有可筛选的类目或标签（线索还没攒够）。
      </div>
    );
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          点击筛选 · 类目"满足任一"、标签"必须都有"、两者 AND
        </div>
        {totalSelected > 0 && (
          <button
            className="text-xs text-indigo-600 hover:underline"
            onClick={onClearAll}
          >
            清除全部筛选（{totalSelected}）
          </button>
        )}
      </div>

      {categories.length > 0 && (
        <ChipRow
          label="类目"
          items={categories}
          selected={selection.categories}
          onToggle={onToggleCategory}
        />
      )}
      {tags.length > 0 && (
        <ChipRow
          label="标签"
          items={tags}
          selected={selection.tags}
          onToggle={onToggleTag}
        />
      )}
    </div>
  );
}

/**
 * Fixed palette of 10 chip color schemes — selection key is hash(name)
 * so the same string ("金融", "USDT", ...) always gets the same color
 * across both rows + page reloads + admin/sales pages.
 *
 * Each entry has two full Tailwind class strings (Tailwind needs to see
 * complete class names at build time — we can't `bg-${color}-50` dynamically).
 *   idle: light bg, colored border + text — "click me"
 *   on:   solid bg + white text — "active filter"
 */
const PALETTE: Array<{ idle: string; on: string }> = [
  { idle: 'bg-rose-50 text-rose-800 border-rose-300 hover:border-rose-500',
    on:   'bg-rose-600 text-white border-rose-600' },
  { idle: 'bg-orange-50 text-orange-800 border-orange-300 hover:border-orange-500',
    on:   'bg-orange-600 text-white border-orange-600' },
  { idle: 'bg-amber-50 text-amber-800 border-amber-300 hover:border-amber-500',
    on:   'bg-amber-600 text-white border-amber-600' },
  { idle: 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:border-emerald-500',
    on:   'bg-emerald-600 text-white border-emerald-600' },
  { idle: 'bg-teal-50 text-teal-800 border-teal-300 hover:border-teal-500',
    on:   'bg-teal-600 text-white border-teal-600' },
  { idle: 'bg-sky-50 text-sky-800 border-sky-300 hover:border-sky-500',
    on:   'bg-sky-600 text-white border-sky-600' },
  { idle: 'bg-blue-50 text-blue-800 border-blue-300 hover:border-blue-500',
    on:   'bg-blue-600 text-white border-blue-600' },
  { idle: 'bg-indigo-50 text-indigo-800 border-indigo-300 hover:border-indigo-500',
    on:   'bg-indigo-600 text-white border-indigo-600' },
  { idle: 'bg-purple-50 text-purple-800 border-purple-300 hover:border-purple-500',
    on:   'bg-purple-600 text-white border-purple-600' },
  { idle: 'bg-pink-50 text-pink-800 border-pink-300 hover:border-pink-500',
    on:   'bg-pink-600 text-white border-pink-600' },
];

/** djb2 string hash — small, deterministic, no deps. Used to pick a
 *  stable palette slot for a name. */
function hashName(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    // h*33 + char — let `| 0` keep it 32-bit so the math stays cheap.
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function colorFor(name: string): { idle: string; on: string } {
  return PALETTE[hashName(name) % PALETTE.length]!;
}

function ChipRow({
  label,
  items,
  selected,
  onToggle,
}: {
  label: string;
  items: Array<{ name: string; count: number }>;
  selected: string[];
  onToggle: (name: string) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-xs text-slate-500 pt-1.5 w-8 flex-shrink-0">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => {
          const isOn = selected.includes(it.name);
          const color = colorFor(it.name);
          return (
            <button
              key={it.name}
              onClick={() => onToggle(it.name)}
              className={`text-xs px-2 py-1 rounded-full border transition ${
                isOn ? color.on : color.idle
              }`}
            >
              {it.name}
              <span className={`ml-1.5 ${isOn ? 'opacity-80' : 'opacity-60'}`}>
                {it.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Helper for parent pages: derive LeadFilterSelection from URLSearchParams
 * and write back.
 */
export function readSelectionFromSearch(
  search: URLSearchParams,
): LeadFilterSelection {
  return {
    categories: (search.get('categories') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    tags: (search.get('tags') ?? '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  };
}

export function writeSelectionToSearch(
  search: URLSearchParams,
  next: LeadFilterSelection,
): URLSearchParams {
  const out = new URLSearchParams(search);
  if (next.categories.length > 0) out.set('categories', next.categories.join(','));
  else out.delete('categories');
  if (next.tags.length > 0) out.set('tags', next.tags.join(','));
  else out.delete('tags');
  return out;
}
